import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    getAiTokenUsageSummary,
    getAiTokenUsage,
    getAiTokenUsageByGroup,
    type AiTokenUsageRecord,
    type AiTokenGroupBroadcast,
} from '@/services/api/ai-token-usage';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
    Coins, TrendingUp, Cpu, Layers, Calendar,
    ChevronLeft, ChevronRight, RefreshCw, Search, X, Users, Send, Globe,
} from 'lucide-react';

const SOURCE_LABELS: Record<string, string> = {
    broadcast: 'Broadcast',
    funnel: 'Funnel',
    node: 'Node',
    auto_reply: 'Auto Reply',
    broadcast_fallback: 'Broadcast (fallback)',
};

const PROVIDER_COLORS: Record<string, string> = {
    openai: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    claude: 'bg-orange-100 text-orange-800 border-orange-200',
    gemini: 'bg-blue-100 text-blue-800 border-blue-200',
    grok: 'bg-gray-100 text-gray-800 border-gray-200',
    deepseek: 'bg-sky-100 text-sky-800 border-sky-200',
};

function formatTokens(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
}

function formatCost(usd: number): string {
    if (usd < 0.01) return `$${usd.toFixed(4)}`;
    return `$${usd.toFixed(2)}`;
}

export default function AiTokenUsage() {
    const [days, setDays] = useState(30);
    const [sourceFilter, setSourceFilter] = useState<string>('all');
    const [providerFilter, setProviderFilter] = useState<string>('all');
    const [page, setPage] = useState(1);
    const [groupId, setGroupId] = useState<number | null>(null);
    const [groupInput, setGroupInput] = useState('');

    const { data: summary, isLoading: loadingSummary } = useQuery({
        queryKey: ['ai-token-usage-summary', days],
        queryFn: () => getAiTokenUsageSummary(days),
    });

    const { data: usageList, isLoading: loadingList, isFetching } = useQuery({
        queryKey: ['ai-token-usage', days, sourceFilter, providerFilter, page],
        queryFn: () =>
            getAiTokenUsage({
                days,
                source_type: sourceFilter !== 'all' ? sourceFilter : undefined,
                provider: providerFilter !== 'all' ? providerFilter : undefined,
                page,
                per_page: 25,
            }),
    });

    const { data: groupData, isLoading: loadingGroup } = useQuery({
        queryKey: ['ai-token-usage-group', groupId],
        queryFn: () => getAiTokenUsageByGroup(groupId!),
        enabled: groupId !== null,
    });

    const totals = summary?.totals;

    return (
        <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <Select value={String(days)} onValueChange={(v) => { setDays(Number(v)); setPage(1); }}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">Últimos 7 dias</SelectItem>
                            <SelectItem value="30">Últimos 30 dias</SelectItem>
                            <SelectItem value="90">Últimos 90 dias</SelectItem>
                            <SelectItem value="365">Último ano</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {isFetching && <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />}
            </div>

            {/* Summary Cards */}
            {loadingSummary ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse h-24" />
                    ))}
                </div>
            ) : totals ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <SummaryCard icon={Layers} label="Chamadas" value={String(totals.calls)} color="blue" />
                    <SummaryCard icon={Cpu} label="Tokens Totais" value={formatTokens(totals.total_tokens)} color="purple" />
                    <SummaryCard icon={TrendingUp} label="Input / Output" value={`${formatTokens(totals.input_tokens)} / ${formatTokens(totals.output_tokens)}`} color="gray" />
                    <SummaryCard icon={Coins} label="Custo Estimado" value={formatCost(totals.cost_usd)} color="green" />
                </div>
            ) : null}

            {/* Breakdown */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* By Provider */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-5">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Por Provider</h3>
                        <div className="space-y-2">
                            {summary.by_provider.length === 0 ? (
                                <p className="text-sm text-gray-400">Sem dados</p>
                            ) : summary.by_provider.map((r) => (
                                <div key={r.provider} className="flex items-center justify-between py-1.5">
                                    <div className="flex items-center gap-2">
                                        <Badge variant="outline" className={cn('text-xs font-semibold', PROVIDER_COLORS[r.provider] || 'bg-gray-100')}>
                                            {r.provider}
                                        </Badge>
                                        <span className="text-xs text-gray-500">{r.calls} chamadas</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-sm font-semibold text-gray-900">{formatTokens(r.tokens)}</span>
                                        <span className="text-xs text-gray-400 ml-2">{formatCost(r.cost_usd)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* By Source */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-5">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Por Tipo</h3>
                        <div className="space-y-2">
                            {summary.by_source.length === 0 ? (
                                <p className="text-sm text-gray-400">Sem dados</p>
                            ) : summary.by_source.map((r) => (
                                <div key={r.source_type} className="flex items-center justify-between py-1.5">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-700">
                                            {SOURCE_LABELS[r.source_type] || r.source_type}
                                        </span>
                                        <span className="text-xs text-gray-400">{r.calls}x</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-sm font-semibold text-gray-900">{formatTokens(r.tokens)}</span>
                                        <span className="text-xs text-gray-400 ml-2">{formatCost(r.cost_usd)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Daily Chart (simple bar) */}
            {summary && summary.daily.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">Tokens por Dia</h3>
                    <div className="flex items-end gap-1 h-32">
                        {(() => {
                            const maxTokens = Math.max(...summary.daily.map(d => d.tokens), 1);
                            return summary.daily.map((d) => (
                                <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${formatTokens(d.tokens)} tokens, ${formatCost(d.cost_usd)}`}>
                                    <div
                                        className="w-full bg-blue-500 rounded-t-sm min-h-[2px] transition-all"
                                        style={{ height: `${Math.max((d.tokens / maxTokens) * 100, 2)}%` }}
                                    />
                                    {summary.daily.length <= 31 && (
                                        <span className="text-[8px] text-gray-400 -rotate-45 origin-top-left whitespace-nowrap">
                                            {d.day.slice(5)}
                                        </span>
                                    )}
                                </div>
                            ));
                        })()}
                    </div>
                </div>
            )}

            {/* Broadcast Group Lookup */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Detalhes por Grupo de Broadcast</h3>
                <div className="flex gap-2">
                    <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Group Broadcast ID..."
                            value={groupInput}
                            onChange={(e) => setGroupInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && groupInput.trim()) {
                                    setGroupId(Number(groupInput.trim()));
                                }
                            }}
                            className="pl-9"
                        />
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => { if (groupInput.trim()) setGroupId(Number(groupInput.trim())); }}
                    >
                        Buscar
                    </Button>
                    {groupId !== null && (
                        <Button variant="ghost" size="icon" onClick={() => { setGroupId(null); setGroupInput(''); }}>
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                </div>

                {loadingGroup && <div className="text-sm text-gray-400">Carregando...</div>}

                {groupData && groupData.group && (
                    <div className="space-y-4">
                        {/* Group Header */}
                        <div className="p-4 bg-gray-50 rounded-xl">
                            <h4 className="font-semibold text-gray-900 text-sm">{groupData.group.title}</h4>
                            <div className="flex flex-wrap gap-4 mt-2">
                                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                    <Layers className="h-3.5 w-3.5" />
                                    {groupData.group.total_broadcasts} broadcasts
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                    <Users className="h-3.5 w-3.5" />
                                    {groupData.group.total_leads.toLocaleString()} leads
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                    <Send className="h-3.5 w-3.5" />
                                    {groupData.group.total_sent.toLocaleString()} enviados
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                    <Cpu className="h-3.5 w-3.5" />
                                    {formatTokens(groupData.tokens.total_tokens)} tokens
                                </div>
                                <div className="flex items-center gap-1.5 text-xs font-medium text-green-700">
                                    <Coins className="h-3.5 w-3.5" />
                                    {formatCost(groupData.tokens.cost_usd)}
                                </div>
                            </div>
                        </div>

                        {/* By Language */}
                        {groupData.by_language.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Custo por Idioma</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                                    {groupData.by_language.map((l) => (
                                        <div key={l.language} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-1.5">
                                                <Globe className="h-3 w-3 text-gray-400" />
                                                <span className="text-xs font-medium text-gray-700 truncate max-w-[80px]">{l.language}</span>
                                            </div>
                                            <span className="text-xs font-semibold text-gray-900">{formatCost(l.cost_usd)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Per Broadcast Table */}
                        {groupData.per_broadcast.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                                    Detalhes por Broadcast ({groupData.per_broadcast.length})
                                </h4>
                                <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-lg border border-gray-100">
                                    <table className="w-full">
                                        <thead className="sticky top-0">
                                            <tr className="bg-gray-50 border-b border-gray-100">
                                                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase px-3 py-2">ID</th>
                                                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase px-3 py-2">Idioma</th>
                                                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase px-3 py-2">Provider</th>
                                                <th className="text-right text-[10px] font-semibold text-gray-500 uppercase px-3 py-2">Tokens</th>
                                                <th className="text-right text-[10px] font-semibold text-gray-500 uppercase px-3 py-2">Custo</th>
                                                <th className="text-right text-[10px] font-semibold text-gray-500 uppercase px-3 py-2">Enviados</th>
                                                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase px-3 py-2">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {groupData.per_broadcast.map((b: AiTokenGroupBroadcast) => (
                                                <tr key={b.broadcast_id} className="hover:bg-gray-50/50">
                                                    <td className="px-3 py-2 text-xs font-mono text-gray-500">{b.broadcast_id}</td>
                                                    <td className="px-3 py-2 text-xs text-gray-700">{b.target_language || '-'}</td>
                                                    <td className="px-3 py-2">
                                                        <Badge variant="outline" className={cn('text-[10px] font-semibold', PROVIDER_COLORS[b.provider] || '')}>
                                                            {b.provider}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-3 py-2 text-xs text-right tabular-nums text-gray-600">{formatTokens(b.total_tokens)}</td>
                                                    <td className="px-3 py-2 text-xs text-right tabular-nums font-medium text-gray-900">{formatCost(b.cost_usd)}</td>
                                                    <td className="px-3 py-2 text-xs text-right tabular-nums text-gray-600">{b.total_sent}</td>
                                                    <td className="px-3 py-2">
                                                        <Badge variant="outline" className={cn('text-[10px]',
                                                            b.broadcast_status === 'sent' ? 'bg-green-50 text-green-700 border-green-200' :
                                                            b.broadcast_status === 'processing' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                                            'bg-gray-50 text-gray-600'
                                                        )}>
                                                            {b.broadcast_status}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {groupData && !groupData.group && (
                    <div className="text-sm text-gray-400">Grupo não encontrado</div>
                )}
            </div>

            {/* Filters + Table */}
            <div className="space-y-3">
                <div className="flex flex-wrap gap-3">
                    <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(1); }}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos os tipos</SelectItem>
                            <SelectItem value="broadcast">Broadcast</SelectItem>
                            <SelectItem value="funnel">Funnel</SelectItem>
                            <SelectItem value="node">Node</SelectItem>
                            <SelectItem value="auto_reply">Auto Reply</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={providerFilter} onValueChange={(v) => { setProviderFilter(v); setPage(1); }}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Provider" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos os providers</SelectItem>
                            <SelectItem value="openai">OpenAI</SelectItem>
                            <SelectItem value="claude">Claude</SelectItem>
                            <SelectItem value="gemini">Gemini</SelectItem>
                            <SelectItem value="grok">Grok</SelectItem>
                            <SelectItem value="deepseek">DeepSeek</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Provider</th>
                                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Modelo</th>
                                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Tipo</th>
                                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Input</th>
                                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Output</th>
                                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Custo</th>
                                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Idioma</th>
                                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Data</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loadingList ? (
                                <tr><td colSpan={8} className="text-center py-8 text-gray-400">Carregando...</td></tr>
                            ) : !usageList?.data?.length ? (
                                <tr><td colSpan={8} className="text-center py-8 text-gray-400">Nenhum registro encontrado</td></tr>
                            ) : usageList.data.map((r: AiTokenUsageRecord) => (
                                <tr key={r.id} className="hover:bg-gray-50/50">
                                    <td className="px-4 py-2.5">
                                        <Badge variant="outline" className={cn('text-xs font-semibold', PROVIDER_COLORS[r.provider] || '')}>
                                            {r.provider}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-2.5 text-xs font-mono text-gray-500">{r.model}</td>
                                    <td className="px-4 py-2.5 text-xs text-gray-700">{SOURCE_LABELS[r.source_type] || r.source_type}</td>
                                    <td className="px-4 py-2.5 text-xs text-right tabular-nums text-gray-600">{formatTokens(r.input_tokens)}</td>
                                    <td className="px-4 py-2.5 text-xs text-right tabular-nums text-gray-600">{formatTokens(r.output_tokens)}</td>
                                    <td className="px-4 py-2.5 text-xs text-right tabular-nums font-medium text-gray-900">{formatCost(r.cost_estimate_usd)}</td>
                                    <td className="px-4 py-2.5 text-xs text-gray-500">{r.target_language || '-'}</td>
                                    <td className="px-4 py-2.5 text-xs text-gray-400">
                                        {r.created_at ? new Date(r.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    {usageList && usageList.total > usageList.per_page && (
                        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
                            <span className="text-xs text-gray-400">
                                {usageList.total} registros
                            </span>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page <= 1}
                                    onClick={() => setPage(p => p - 1)}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <span className="text-xs text-gray-600">
                                    {page} / {Math.ceil(usageList.total / usageList.per_page)}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page >= Math.ceil(usageList.total / usageList.per_page)}
                                    onClick={() => setPage(p => p + 1)}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function SummaryCard({ icon: Icon, label, value, color }: {
    icon: React.ElementType;
    label: string;
    value: string;
    color: 'blue' | 'purple' | 'green' | 'gray';
}) {
    const colors = {
        blue: 'bg-blue-50 text-blue-600',
        purple: 'bg-purple-50 text-purple-600',
        green: 'bg-green-50 text-green-600',
        gray: 'bg-gray-50 text-gray-600',
    };
    return (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-2">
                <div className={cn('h-7 w-7 rounded-lg flex items-center justify-center', colors[color])}>
                    <Icon className="h-3.5 w-3.5" />
                </div>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</span>
            </div>
            <div className="text-xl font-bold text-gray-900">{value}</div>
        </div>
    );
}
